import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Chart as ChartJS, BarElement, CategoryScale, LinearScale, Tooltip,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import {
  useGetAumSummaryQuery,
  useGetAumTrendQuery,
  useGetAumPlatformQuery,
  useGetAumStylesQuery,
  useGetAumFirmAdvisorsQuery,
  useListAumFirmsQuery,
  useGetAumStatusQuery,
  useGetAumHouseholdsQuery,
  useGetAumHouseholdOrionQuery,
  useLazyGetAumHouseholdsCsvQuery,
  useGetAumNotInFscQuery,
  useGetAumBreakdownQuery,
} from '../../app/api';
import { useIsMobile } from '../utils';

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip);

// The Work palette — flat, hairline borders, goldenrod as an accent rather than
// a header colour. Kept in step with routes/work/Home.js.
const work = {
  deepBlue:  '#0a2947',
  tealBlue:  '#0069a6',
  goldenrod: '#d09b2c',
  ink:       '#0a2947',
  muted:     '#6b7280',
  hairline:  '#dde2e8',
  hairlineStrong: '#c4cbd4',
  surface:   '#ffffff',
  canvas:    '#f3f5f8',
};

// Diverging pair for the net-flow chart: money in vs money out is POLARITY, so
// it takes two hues that read as opposite either side of a neutral zero line —
// never two cool hues, and never a hue at the midpoint.
//
// Validated (light mode, #ffffff surface): CVD ΔE 16.5 protan / 30.3 normal,
// both poles ≥3:1 contrast. The positive pole is the brand's own tealBlue.
const flowPositive = '#0069a6';
const flowNegative = '#d03b3b';
const gridline = '#eef1f5';

const RANGES = [
  { key: 'mtd',  label: 'This month' },
  { key: 'qtd',  label: 'Quarter to date' },
  { key: 'ytd',  label: 'Year to date' },
  { key: 't12',  label: 'Trailing 12 months' },
  // A named month or an explicit span. Reconciliation is always against a
  // specific period — someone checking their own July report needs July, not
  // "July and August because that is what year-to-date happens to cover".
  { key: 'custom', label: 'Custom' },
];

function monthStart(d) {
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1));
}

function iso(d) {
  return d.toISOString().slice(0, 10);
}

// A preset resolves to the same whole-month bounds the API works in, so what
// the page asks for and what the cache stores can never be a half-month apart.
function resolveRange(key) {
  const now = monthStart(new Date());
  const y = now.getUTCFullYear();
  const m = now.getUTCMonth();
  switch (key) {
    case 'mtd': return { start: iso(now), end: iso(now) };
    case 'qtd': return { start: iso(new Date(Date.UTC(y, Math.floor(m / 3) * 3, 1))), end: iso(now) };
    case 'ytd': return { start: iso(new Date(Date.UTC(y, 0, 1))), end: iso(now) };
    default:    return { start: iso(new Date(Date.UTC(y, m - 11, 1))), end: iso(now) };
  }
}

// Auto-compacted money, the stat-tile contract: $4.2M rather than $4,231,908.
// `full` is what the table and tooltips use, where the exact figure is the point.
function money(n, { full = false, signed = false } = {}) {
  const v = Number(n) || 0;
  const sign = signed && v > 0 ? '+' : v < 0 ? '−' : '';
  const abs = Math.abs(v);
  if (full) {
    return `${sign}$${abs.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  }
  if (abs >= 1e9) return `${sign}$${(abs / 1e9).toFixed(abs >= 1e10 ? 0 : 1)}B`;
  if (abs >= 1e6) return `${sign}$${(abs / 1e6).toFixed(abs >= 1e7 ? 0 : 1)}M`;
  if (abs >= 1e3) return `${sign}$${(abs / 1e3).toFixed(0)}K`;
  return `${sign}$${abs.toFixed(0)}`;
}

function monthLabel(isoDate) {
  const d = new Date(`${isoDate}T00:00:00Z`);
  return d.toLocaleDateString(undefined, { month: 'short', year: '2-digit', timeZone: 'UTC' });
}

/**
 * A sync timestamp, in the reader's own zone.
 *
 * MySQL DATETIMEs are UTC but carry no offset, and JS parses an offset-less
 * date-time as LOCAL — so `new Date(value)` alone silently shifts every
 * timestamp by the viewer's offset and reports stale data as fresh (or the
 * reverse). The Z goes on before parsing; `toLocaleString` does the conversion.
 */
function syncedAt(ts) {
  if (!ts) return null;
  const raw = String(ts).replace(' ', 'T');
  const d = new Date(/[Z+]|-\d{2}:\d{2}$/.test(raw) ? raw : `${raw}Z`);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleString();
}

/**
 * One headline number. `hero` renders the single figure the page leads with;
 * the rest are supporting tiles.
 *
 * Values use proportional figures deliberately — `tabular-nums` gives every
 * digit the width of a zero, which makes a large standalone number look loose.
 * Tabular figures belong in the table below, where columns must align.
 */
function StatTile({ label, value, delta, deltaLabel, note, hero = false, upIsGood = true }) {
  const showDelta = delta !== null && delta !== undefined && Number.isFinite(delta);
  const good = upIsGood ? delta >= 0 : delta <= 0;
  return (
    <div style={{
      background: work.surface,
      border: `1px solid ${work.hairline}`,
      borderTop: hero ? `3px solid ${work.deepBlue}` : `1px solid ${work.hairline}`,
      borderRadius: 6,
      padding: hero ? '1.15rem 1.25rem' : '0.9rem 1rem',
    }}>
      <div style={{
        color: work.muted, fontSize: '0.72rem', fontWeight: 700,
        textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 6,
      }}>
        {label}
      </div>
      <div style={{
        color: work.ink, fontWeight: 700, lineHeight: 1.1,
        fontSize: hero ? '2.6rem' : '1.5rem',
      }}>
        {value}
      </div>
      {showDelta && (
        <div style={{ marginTop: 6, fontSize: '0.78rem', color: work.muted }}>
          {/* The arrow carries direction as well as the colour, so the
              comparison is never colour-alone. */}
          <span style={{ color: good ? '#1a7f4e' : flowNegative, fontWeight: 700 }}>
            <i className={`bi bi-arrow-${delta >= 0 ? 'up' : 'down'}-short`} />
            {money(delta, { signed: true })}
          </span>
          {' '}vs {deltaLabel}
        </div>
      )}
      {note && (
        <div style={{ marginTop: 6, fontSize: '0.78rem', color: work.muted }}>{note}</div>
      )}
    </div>
  );
}

/**
 * Net New AUM — net flows and AUM by firm and advisor.
 *
 * Reads the local `orion_flows` cache (see api/app/routers/orion_aum.py), which
 * a nightly job refreshes from Orion. The footer says when, so the page is
 * honest about its own staleness rather than implying it is live.
 *
 * NET FLOWS ARE THE HEADLINE — contributions minus withdrawals, market movement
 * excluded. AUM change sits beside it rather than instead of it: a quarter can
 * show a rising book and negative flows at the same time, and that difference is
 * the whole reason anyone opens this page.
 *
 * Firm and advisor come from the Salesforce FSC household join, so what a given
 * person can see here is exactly what they can see in the client book.
 */
export default function NetNewAum() {
  const isMobile = useIsMobile();
  const [rangeKey, setRangeKey] = useState('t12');
  // Month strings ("2026-07") rather than dates: the cache is monthly, so
  // offering a day would imply a precision the data does not have.
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [firm, setFirm] = useState('');
  const [groupBy, setGroupBy] = useState('firm');
  const [expanded, setExpanded] = useState(null);
  const [sort, setSort] = useState({ field: 'net_flow', dir: 'desc' });
  const [chartAsTable, setChartAsTable] = useState(false);

  const range = useMemo(() => {
    if (rangeKey !== 'custom') return resolveRange(rangeKey);
    // An incomplete custom range falls back to the preset rather than sending a
    // half-specified period the API would reject. Leaving the end blank means
    // "just that month", which is the common case for reconciliation.
    if (!customStart) return resolveRange('t12');
    return { start: `${customStart}-01`, end: `${customEnd || customStart}-01` };
  }, [rangeKey, customStart, customEnd]);
  const params = { ...range, group_by: groupBy, firm: firm || undefined };

  const { data: summary, isFetching } = useGetAumSummaryQuery(params);
  const { data: trend } = useGetAumTrendQuery({ ...range, firm: firm || undefined });
  const { data: firms } = useListAumFirmsQuery();
  const { data: status } = useGetAumStatusQuery();

  const rows = useMemo(() => {
    const list = [...(summary?.rows || [])];
    const dir = sort.dir === 'asc' ? 1 : -1;
    list.sort((a, b) => {
      const av = a[sort.field]; const bv = b[sort.field];
      if (typeof av === 'string') return av.localeCompare(bv) * dir;
      return ((av || 0) - (bv || 0)) * dir;
    });
    return list;
  }, [summary, sort]);

  const totals = summary?.totals;
  const prior = summary?.prior;
  const coverage = summary?.coverage;
  const months = trend?.months || [];

  // Market values come from a different Orion source than flows (an as-of query,
  // run per month-end) and may not be loaded yet. Rendering an unpopulated
  // column as "$0" would assert that a firm's book is empty, which is a far
  // worse error than not showing the column at all — so the AUM columns and the
  // Ending AUM tile appear only once there is something real in them.
  const hasAum = useMemo(
    () => (summary?.rows || []).some((r) => Number(r.ending_aum) > 0),
    [summary],
  );

  // Today's book, from Orion's household snapshot as of the last sync. A
  // different thing from Ending AUM above: it ignores the period entirely, so
  // it is labelled with its date and never differenced into an AUM change.
  const hasCurrentAum = Number(summary?.totals?.current_aum) > 0;
  const aumAsOf = syncedAt(status?.last_sync?.finished);

  const chartData = useMemo(() => ({
    labels: months.map((m) => monthLabel(m.period_month)),
    datasets: [{
      data: months.map((m) => Number(m.net_flow) || 0),
      // Colour follows the sign, which is what the bar's side of the baseline
      // already shows — the two channels agree, so neither is load-bearing alone.
      backgroundColor: months.map((m) => (Number(m.net_flow) >= 0 ? flowPositive : flowNegative)),
      // 4px rounded data-end, square at the baseline: `borderSkipped: false`
      // plus a per-corner radius would round both ends, so the far end only.
      borderRadius: { topLeft: 4, topRight: 4, bottomLeft: 0, bottomRight: 0 },
      borderSkipped: 'start',
      maxBarThickness: 24,
      // Leaves air in each band rather than filling it — the gap between bars is
      // the surface showing through, not a stroke drawn around them.
      categoryPercentage: 0.7,
      barPercentage: 0.9,
    }],
  }), [months]);

  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      // One series, so no legend box — the card's title says what is plotted.
      legend: { display: false },
      tooltip: {
        backgroundColor: work.deepBlue,
        padding: 10,
        displayColors: false,
        callbacks: {
          title: (items) => items[0]?.label,
          label: (item) => {
            const m = months[item.dataIndex] || {};
            return [
              `Net flow  ${money(m.net_flow, { full: true, signed: true })}`,
              `Inflows   ${money(m.inflows, { full: true })}`,
              `Outflows  ${money(m.outflows, { full: true })}`,
            ];
          },
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        border: { color: work.hairline },
        ticks: { color: work.muted, font: { size: 11 } },
      },
      y: {
        // Hairline, solid, one step off the surface — recessive by design.
        grid: { color: gridline, drawTicks: false },
        border: { display: false },
        ticks: {
          color: work.muted,
          font: { size: 11 },
          callback: (v) => money(v),
        },
      },
    },
  }), [months]);

  const sortBy = (field) => setSort((s) => (
    s.field === field ? { field, dir: s.dir === 'desc' ? 'asc' : 'desc' } : { field, dir: 'desc' }
  ));

  const th = (label, field, align = 'right') => (
    <th
      onClick={() => sortBy(field)}
      style={{
        padding: '0.5rem 0.6rem', textAlign: align, cursor: 'pointer',
        fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase',
        letterSpacing: '0.5px', color: work.muted, whiteSpace: 'nowrap',
        borderBottom: `1px solid ${work.hairlineStrong}`,
      }}
    >
      {label}
      {sort.field === field && (
        <i className={`bi bi-caret-${sort.dir === 'desc' ? 'down' : 'up'}-fill`}
           style={{ marginLeft: 4, fontSize: '0.6rem' }} />
      )}
    </th>
  );

  const num = { padding: '0.5rem 0.6rem', textAlign: 'right', fontVariantNumeric: 'tabular-nums' };

  return (
    <div style={{
      background: work.canvas, minHeight: '100vh',
      padding: isMobile ? '1rem' : '1.5rem 1.75rem', color: work.ink,
    }}>
      {/* Header */}
      <div style={{
        background: work.surface, border: `1px solid ${work.hairline}`,
        borderTop: `3px solid ${work.deepBlue}`, borderRadius: 6,
        padding: isMobile ? '1.1rem' : '1.4rem 1.6rem', marginBottom: '1rem',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.85rem' }}>
          <div style={{
            background: work.deepBlue, color: 'white', width: 44, height: 44,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            borderRadius: 6, flexShrink: 0,
          }}>
            <i className="bi bi-graph-up-arrow" style={{ fontSize: '1.3rem' }} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{
              color: work.muted, fontSize: '0.72rem', fontWeight: 700,
              textTransform: 'uppercase', letterSpacing: '1.5px', margin: '0 0 2px',
            }}>
              Reports
            </p>
            <h3 style={{ margin: 0, fontWeight: 800, fontSize: isMobile ? '1.2rem' : '1.4rem' }}>
              Net New AUM - Work In Progress, DO NOT USE VALUES
            </h3>
          </div>
        </div>
      </div>

      {/* One filter row above everything it scopes — never a filter inside a
          chart card, so the chart and the table always show the same slice. */}
      <div style={{
        background: work.surface, border: `1px solid ${work.hairline}`,
        borderRadius: 6, padding: '0.75rem 0.9rem', marginBottom: '1rem',
        display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'center',
      }}>
        <div style={{ display: 'flex', gap: 4 }}>
          {RANGES.map((r) => (
            <button
              key={r.key}
              type="button"
              onClick={() => setRangeKey(r.key)}
              style={{
                padding: '0.3rem 0.65rem', fontSize: '0.78rem', fontWeight: 600,
                borderRadius: 4, cursor: 'pointer',
                border: `1px solid ${rangeKey === r.key ? work.deepBlue : work.hairline}`,
                background: rangeKey === r.key ? work.deepBlue : work.surface,
                color: rangeKey === r.key ? 'white' : work.muted,
              }}
            >
              {r.label}
            </button>
          ))}
        </div>
        {rangeKey === 'custom' && (
          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <input
              type="month"
              value={customStart}
              onChange={(e) => setCustomStart(e.target.value)}
              max={customEnd || undefined}
              style={{
                padding: '0.25rem 0.4rem', fontSize: '0.82rem', borderRadius: 4,
                border: `1px solid ${work.hairline}`, color: work.ink,
              }}
            />
            <span style={{ color: work.muted, fontSize: '0.8rem' }}>to</span>
            <input
              type="month"
              value={customEnd}
              onChange={(e) => setCustomEnd(e.target.value)}
              min={customStart || undefined}
              // Blank means "the same month" — the single-month case is what
              // anyone reconciling a monthly report actually wants.
              placeholder={customStart}
              style={{
                padding: '0.25rem 0.4rem', fontSize: '0.82rem', borderRadius: 4,
                border: `1px solid ${work.hairline}`, color: work.ink,
              }}
            />
          </span>
        )}
        <select
          value={firm}
          onChange={(e) => { setFirm(e.target.value); setExpanded(null); }}
          style={{
            padding: '0.3rem 0.5rem', fontSize: '0.82rem', borderRadius: 4,
            border: `1px solid ${work.hairline}`, color: work.ink, background: work.surface,
          }}
        >
          <option value="">All firms</option>
          {(firms?.firms || []).map((f) => (
            <option key={f.id} value={f.id}>{f.name}</option>
          ))}
        </select>
        <select
          value={groupBy}
          onChange={(e) => { setGroupBy(e.target.value); setExpanded(null); }}
          style={{
            padding: '0.3rem 0.5rem', fontSize: '0.82rem', borderRadius: 4,
            border: `1px solid ${work.hairline}`, color: work.ink, background: work.surface,
          }}
        >
          <option value="firm">By firm</option>
          <option value="advisor">By advisor</option>
        </select>
        {isFetching && (
          <span style={{ color: work.muted, fontSize: '0.78rem' }}>
            <i className="bi bi-arrow-repeat" /> updating…
          </span>
        )}
      </div>

      {/* Above the numbers it qualifies, not below them: these households ARE in
          the totals, and someone has to go and create them in Salesforce. */}
      <NotInFsc range={range} firm={firm} coverage={coverage} />

      {/* KPI row — the hero is net new assets; the rest supply the context that
          keeps it honest. */}
      <div style={{
        display: 'grid', gap: '0.75rem', marginBottom: '1rem',
        gridTemplateColumns: isMobile ? '1fr' : '1.4fr 1fr 1fr 1fr',
      }}>
        <StatTile
          hero
          label="Net new assets"
          value={money(totals?.net_flow, { signed: true })}
          // Only compare when the prior window actually HAS data. The cache
          // holds a trailing 13 months, so the period before a trailing-12 view
          // is usually empty — and differencing against nothing would render the
          // whole period's flows as if they were growth over last year.
          delta={prior?.totals?.groups > 0
            ? (totals?.net_flow || 0) - (prior.totals.net_flow || 0)
            : null}
          deltaLabel="prior period"
        />
        <StatTile label="Inflows" value={money(totals?.inflows)} />
        <StatTile label="Outflows" value={money(totals?.outflows)} upIsGood={false} />
        {hasAum ? (
          <StatTile
            label="Ending AUM"
            value={money(totals?.ending_aum)}
            delta={totals?.aum_delta}
            deltaLabel="start of period"
          />
        ) : hasCurrentAum ? (
          <StatTile
            label="Current AUM"
            value={money(totals?.current_aum)}
            note={`${(totals?.aum_households || 0).toLocaleString()} households${aumAsOf ? ` - as of ${aumAsOf}` : ''}`}
          />
        ) : (
          <StatTile label="Households" value={(totals?.households || 0).toLocaleString()} />
        )}
      </div>

      {/* Net flows by month */}
      <div style={{
        background: work.surface, border: `1px solid ${work.hairline}`,
        borderRadius: 6, padding: '1rem 1.1rem', marginBottom: '1rem',
      }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.6rem', marginBottom: '0.75rem' }}>
          <h5 style={{ margin: 0, fontSize: '0.95rem', fontWeight: 700 }}>
            Net flows by month
          </h5>
          <span style={{ color: work.muted, fontSize: '0.78rem' }}>
            contributions less withdrawals · market movement excluded
          </span>
          {/* The table twin. A tooltip enhances; it never gates a value. */}
          <button
            type="button"
            onClick={() => setChartAsTable((v) => !v)}
            style={{
              marginLeft: 'auto', padding: '0.2rem 0.5rem', fontSize: '0.72rem',
              fontWeight: 600, borderRadius: 4, cursor: 'pointer',
              border: `1px solid ${work.hairline}`, background: work.surface, color: work.muted,
            }}
          >
            <i className={`bi bi-${chartAsTable ? 'bar-chart' : 'table'}`} />
            {chartAsTable ? ' Chart' : ' Table'}
          </button>
        </div>

        {chartAsTable ? (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', fontSize: '0.85rem', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ padding: '0.4rem 0.6rem', textAlign: 'left', fontSize: '0.7rem', color: work.muted, textTransform: 'uppercase' }}>Month</th>
                  <th style={{ padding: '0.4rem 0.6rem', textAlign: 'right', fontSize: '0.7rem', color: work.muted, textTransform: 'uppercase' }}>Inflows</th>
                  <th style={{ padding: '0.4rem 0.6rem', textAlign: 'right', fontSize: '0.7rem', color: work.muted, textTransform: 'uppercase' }}>Outflows</th>
                  <th style={{ padding: '0.4rem 0.6rem', textAlign: 'right', fontSize: '0.7rem', color: work.muted, textTransform: 'uppercase' }}>Net flow</th>
                </tr>
              </thead>
              <tbody>
                {months.map((m) => (
                  <tr key={m.period_month} style={{ borderTop: `1px solid ${work.hairline}` }}>
                    <td style={{ padding: '0.4rem 0.6rem' }}>{monthLabel(m.period_month)}</td>
                    <td style={num}>{money(m.inflows, { full: true })}</td>
                    <td style={num}>{money(m.outflows, { full: true })}</td>
                    <td style={{ ...num, fontWeight: 700 }}>{money(m.net_flow, { full: true, signed: true })}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          // Sized to include the x-axis band, so the card never grows a tiny
          // nested scrollbar to reach the month labels.
          <div style={{ height: 260 }}>
            <Bar data={chartData} options={chartOptions} />
          </div>
        )}
      </div>

      {/* Firm / advisor table. 66 offices is far past the point where colour
          classes stop being readable, so this is a table rather than more hues. */}
      <div style={{
        background: work.surface, border: `1px solid ${work.hairline}`,
        borderRadius: 6, overflow: 'hidden',
      }}>
        <div style={{ padding: '0.85rem 1.1rem', borderBottom: `1px solid ${work.hairline}` }}>
          <h5 style={{ margin: 0, fontSize: '0.95rem', fontWeight: 700 }}>
            {groupBy === 'firm' ? 'By firm' : 'By advisor'}
            <span style={{ color: work.muted, fontWeight: 400, fontSize: '0.8rem', marginLeft: 8 }}>
              {rows.length} {groupBy === 'firm' ? 'offices' : 'advisors'}
            </span>
          </h5>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', fontSize: '0.85rem', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {th(groupBy === 'firm' ? 'Firm' : 'Advisor', 'group_name', 'left')}
                {groupBy === 'advisor' && th('Firm', 'firm_name', 'left')}
                {th('Net new assets', 'net_flow')}
                {th('Inflows', 'inflows')}
                {th('Outflows', 'outflows')}
                {hasAum && th('AUM change', 'aum_delta')}
                {hasAum && th('Ending AUM', 'ending_aum')}
                {hasCurrentAum && th('Current AUM', 'current_aum')}
                {th('Households', 'households')}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <React.Fragment key={`${r.group_id}-${r.firm_name || ''}`}>
                  <tr
                    onClick={groupBy === 'firm'
                      ? () => setExpanded(expanded === r.group_id ? null : r.group_id)
                      : undefined}
                    style={{
                      borderTop: `1px solid ${work.hairline}`,
                      cursor: groupBy === 'firm' ? 'pointer' : 'default',
                      background: expanded === r.group_id ? work.canvas : 'transparent',
                    }}
                  >
                    <td style={{ padding: '0.5rem 0.6rem', fontWeight: 600 }}>
                      {groupBy === 'firm' && (
                        <i className={`bi bi-chevron-${expanded === r.group_id ? 'down' : 'right'}`}
                           style={{ color: work.muted, fontSize: '0.7rem', marginRight: 6 }} />
                      )}
                      {r.group_name || '—'}
                      {/* This firm's number leans on households Salesforce has
                          no record of — say so on the row, not only in the
                          banner, or the drill-down looks short for no reason. */}
                      {r.households_not_in_fsc > 0 && (
                        <span
                          title={`${r.households_not_in_fsc} household(s) counted here have no Salesforce record `
                                 + `(${money(r.net_flow_not_in_fsc, { full: true, signed: true })} net)`}
                          style={{
                            marginLeft: 6, padding: '0.05rem 0.35rem', borderRadius: 3,
                            fontSize: '0.68rem', fontWeight: 700,
                            background: '#fffaf0', color: work.goldenrod,
                            border: `1px solid ${work.goldenrod}`,
                          }}
                        >
                          {r.households_not_in_fsc} not in FSC
                        </span>
                      )}
                    </td>
                    {groupBy === 'advisor' && (
                      <td style={{ padding: '0.5rem 0.6rem', color: work.muted }}>{r.firm_name || '—'}</td>
                    )}
                    <td style={{ ...num, fontWeight: 700 }}>
                      {money(r.net_flow, { full: true, signed: true })}
                    </td>
                    <td style={num}>{money(r.inflows, { full: true })}</td>
                    <td style={num}>{money(r.outflows, { full: true })}</td>
                    {hasAum && (
                      <td style={num}>{money(r.aum_delta, { full: true, signed: true })}</td>
                    )}
                    {hasAum && <td style={num}>{money(r.ending_aum, { full: true })}</td>}
                    {hasCurrentAum && <td style={num}>{money(r.current_aum, { full: true })}</td>}
                    <td style={num}>{r.households}</td>
                  </tr>
                  {expanded === r.group_id && (
                    <AdvisorRows officeId={r.group_id} range={range} numStyle={num}
                                 hasAum={hasAum} hasCurrentAum={hasCurrentAum} />
                  )}
                </React.Fragment>
              ))}
              {!rows.length && !isFetching && (
                <tr>
                  <td colSpan={5 + (groupBy === 'advisor' ? 1 : 0) + (hasAum ? 2 : 0)
                               + (hasCurrentAum ? 1 : 0)}
                      style={{ padding: '2rem', textAlign: 'center', color: work.muted }}>
                    {status?.scope_is_empty
                      ? 'No offices are assigned to your Work access yet — ask an administrator to set your scope.'
                      : 'No flow data for this period.'}
                  </td>
                </tr>
              )}
            </tbody>
            {rows.length > 0 && (
              <tfoot>
                <tr style={{ borderTop: `2px solid ${work.hairlineStrong}`, fontWeight: 700 }}>
                  <td style={{ padding: '0.5rem 0.6rem' }}>Total</td>
                  {groupBy === 'advisor' && <td />}
                  <td style={num}>{money(totals?.net_flow, { full: true, signed: true })}</td>
                  <td style={num}>{money(totals?.inflows, { full: true })}</td>
                  <td style={num}>{money(totals?.outflows, { full: true })}</td>
                  {hasAum && (
                    <td style={num}>{money(totals?.aum_delta, { full: true, signed: true })}</td>
                  )}
                  {hasAum && <td style={num}>{money(totals?.ending_aum, { full: true })}</td>}
                  {hasCurrentAum && <td style={num}>{money(totals?.current_aum, { full: true })}</td>}
                  <td style={num}>{totals?.households}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      {/* Where the money moved: on PCA's platform or on AssetMark / OPS /
          Evolve. Sits above the type breakdown because it splits the SAME
          counted number rather than explaining what went into it. */}
      <PlatformSplit range={range} firm={firm} />
      <StyleSplit range={range} firm={firm} />

      {/* The composition of the headline, on request. Collapsed by default so
          the page stays a dashboard, but one click from "what did you count?" */}
      <Breakdown range={range} firm={firm} />

      {/* Staleness and coverage, stated rather than implied. Both counts mean a
          total above is incomplete — a firm whose book sits in an Orion database
          these credentials cannot reach would surface here, not as a zero. */}
      <div style={{
        marginTop: '0.75rem', color: work.muted, fontSize: '0.78rem',
        display: 'flex', flexWrap: 'wrap', gap: '0.25rem 1rem',
      }}>
        <span>
          <i className="bi bi-clock-history" />{' '}
          {syncedAt(status?.last_sync?.finished)
            ? `Last updated ${syncedAt(status.last_sync.finished)}`
            : 'Never synced from Orion'}
        </span>
        {coverage?.households_with_flows > 0 && (
          <span>{coverage.households_with_flows.toLocaleString()} households with activity</span>
        )}
        {/* Absence needs explaining. A missing column reads as a bug unless the
            page says why it is missing. */}
        {!hasAum && (
          <span>
            <i className="bi bi-info-circle" />{' '}
            Month-end AUM is not loaded yet, so there is no AUM change column.
            Current AUM is today&apos;s book as of the last sync. Net flows are unaffected.
          </span>
        )}
        {coverage?.households_without_flows > 0 && (
          <span style={{ color: work.goldenrod }}>
            <i className="bi bi-exclamation-triangle-fill" />{' '}
            {coverage.households_without_flows.toLocaleString()} in-scope households have no Orion rows this period
          </span>
        )}
        {/* Left OUT of every total on purpose: a broker dealer that is not a PCA
            office. Counted here so the exclusion is visible rather than assumed. */}
        {coverage?.unmapped_office_households > 0 && (
          <span>
            <i className="bi bi-info-circle" />{' '}
            {coverage.unmapped_office_households.toLocaleString()} households sit under a
            broker dealer that is not a PCA office — excluded from these totals
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * The households in these totals that Salesforce has never heard of.
 *
 * Flows are attributed through Orion's office, so a household with no Salesforce
 * record still counts — before that change, 43 of them carrying $7.9M of July
 * net new assets were reported as zero. Counting them is right; hiding that we
 * are counting them would not be. Every one measured was a funded new client the
 * custodian feed had created in Orion that nobody had entered in Salesforce, so
 * this is a work list, not a warning: the fix is to create the household.
 *
 * Rendered as a banner rather than a footnote because it is the one thing on the
 * page that asks someone to go and do something.
 */
//: The three platform buckets, in a fixed order that never depends on the data.
//: Off-platform first because it is the one anyone opens this panel to see.
const PLATFORMS = [
  {
    key: 'off',
    label: 'Off platform',
    note: 'AssetMark, Orion Portfolio Solutions, Mutual Securities (Evolve)',
  },
  {
    key: 'on',
    label: 'On platform',
    note: 'PCA models and directly-held accounts',
  },
  {
    key: 'unknown',
    label: 'Unknown',
    note: 'no management style on the transaction or its account',
  },
];

/**
 * On platform or off — the split of the counted net flow.
 *
 * WHY THIS CAN BE READ AT ALL. Orion stamps the account's management style on
 * every TRANSACTION row, so the platform travels with the money and survives
 * whatever happens to the account afterwards. The parallel spreadsheet build
 * has to join transactions to an active-only account export instead, and loses
 * 28,636 rows worth -$219M that way — the closed accounts of departed advisors,
 * which is exactly the population the number turns on.
 *
 * MIGRATION (off → on) IS DELIBERATELY NOT HERE. It needs where an account sat
 * BEFORE, and Orion only ever reports an account as it is today. The hub records
 * that from its first sync forward (`orion_account_history`), so this becomes
 * answerable with time and cannot be backfilled. Claiming it now would mean
 * inferring it, and every available inference is wrong in the flattering
 * direction.
 *
 * Bars use the page's own polarity pair rather than a colour per platform: a
 * bucket's net flow can be negative — an advisor exit is mostly off-platform
 * outflow — so a share-of-total bar would be a lie, and identity is carried by
 * the row label rather than by a hue.
 */
function PlatformSplit({ range, firm }) {
  const [open, setOpen] = useState(false);
  const { data, isFetching } = useGetAumPlatformQuery(
    { ...range, firm: firm || undefined }, { skip: !open },
  );

  const buckets = data?.buckets || {};
  const rows = PLATFORMS.map((p) => ({ ...p, ...(buckets[p.key] || {}) }));
  // A shared zero axis across the three bars, scaled to the largest absolute
  // value. Each bar is then comparable to the others, which a per-row scale
  // would quietly destroy.
  const widest = Math.max(1, ...rows.map((r) => Math.abs(r.net_flow || 0)));
  const coverage = data?.account_coverage;
  // Until a live sync has repopulated `management_style` over the window, every
  // stored row resolves to unknown. Saying so is the difference between a panel
  // that is empty and a panel that is wrong.
  const unknownShare = data?.unknown_share;
  const mostlyUnknown = typeof unknownShare === 'number' && unknownShare > 0.2;

  return (
    <div style={{
      background: work.surface, border: `1px solid ${work.hairline}`,
      borderRadius: 6, marginTop: '0.75rem',
    }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: '0.5rem',
          padding: '0.7rem 0.9rem', background: 'transparent', border: 0,
          cursor: 'pointer', color: work.ink, fontSize: '0.84rem', fontWeight: 700,
          textAlign: 'left',
        }}
      >
        <i className={`bi bi-chevron-${open ? 'down' : 'right'}`} />
        On platform or off
        <span style={{ color: work.muted, fontWeight: 400 }}>
          — AssetMark, OPS and Evolve against PCA&apos;s own book
        </span>
      </button>

      {open && (
        <div style={{ padding: '0 0.9rem 0.9rem' }}>
          {isFetching && <div style={{ color: work.muted, fontSize: '0.82rem' }}>Loading…</div>}

          {!isFetching && mostlyUnknown && (
            <div style={{
              background: '#fffaf0', border: `1px solid ${work.goldenrod}`,
              borderRadius: 6, padding: '0.6rem 0.8rem', marginBottom: '0.8rem',
              fontSize: '0.8rem',
            }}>
              <i className="bi bi-exclamation-triangle-fill" style={{ color: work.goldenrod }} />
              {' '}
              <strong>{Math.round(unknownShare * 100)}% of this period is unplaced.</strong>
              {' '}
              Platform is read from a field the sync only began storing on
              2026-09-18, so months cached before that carry none. A full live
              sync over the window fills it in; until then this split is
              incomplete rather than wrong.
            </div>
          )}

          {!isFetching && (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
              <thead>
                <tr style={{ color: work.muted, textAlign: 'left' }}>
                  <th style={{ padding: '0.3rem 0.5rem' }}>Platform</th>
                  <th style={{ padding: '0.3rem 0.5rem', width: '32%' }} />
                  <th style={{ padding: '0.3rem 0.5rem', textAlign: 'right' }}>Net flow</th>
                  <th style={{ padding: '0.3rem 0.5rem', textAlign: 'right' }}>In</th>
                  <th style={{ padding: '0.3rem 0.5rem', textAlign: 'right' }}>Out</th>
                  <th style={{ padding: '0.3rem 0.5rem', textAlign: 'right' }}>Households</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const net = r.net_flow || 0;
                  const pct = (Math.abs(net) / widest) * 50;
                  return (
                    <tr key={r.key} style={{ borderTop: `1px solid ${work.hairline}` }}>
                      <td style={{ padding: '0.45rem 0.5rem' }}>
                        <div style={{ fontWeight: 600 }}>{r.label}</div>
                        <div style={{ color: work.muted, fontSize: '0.72rem' }}>{r.note}</div>
                      </td>
                      <td style={{ padding: '0.45rem 0.5rem' }}>
                        {/* A shared zero line down the middle, so a negative
                            bucket reads as negative rather than as a small
                            positive one. */}
                        <div style={{
                          position: 'relative', height: 14,
                          borderLeft: `1px solid ${work.hairline}`,
                          borderRight: `1px solid ${work.hairline}`,
                        }}>
                          <div style={{
                            position: 'absolute', left: '50%', top: 0, bottom: 0,
                            width: 1, background: work.hairlineStrong,
                          }} />
                          <div
                            style={{
                              position: 'absolute', top: 2, bottom: 2,
                              width: `${pct}%`,
                              left: net >= 0 ? '50%' : `${50 - pct}%`,
                              background: net >= 0 ? flowPositive : flowNegative,
                              borderRadius: net >= 0 ? '0 3px 3px 0' : '3px 0 0 3px',
                            }}
                          />
                        </div>
                      </td>
                      <td style={{
                        padding: '0.45rem 0.5rem', textAlign: 'right',
                        fontVariantNumeric: 'tabular-nums', fontWeight: 700,
                      }}>
                        {money(net, { full: true, signed: true })}
                      </td>
                      <td style={{ padding: '0.45rem 0.5rem', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                        {money(r.inflows || 0, { full: true })}
                      </td>
                      <td style={{ padding: '0.45rem 0.5rem', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                        {money(r.outflows || 0, { full: true })}
                      </td>
                      <td style={{ padding: '0.45rem 0.5rem', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                        {(r.households || 0).toLocaleString()}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr style={{ borderTop: `2px solid ${work.hairlineStrong}`, fontWeight: 700 }}>
                  <td style={{ padding: '0.45rem 0.5rem' }} colSpan={2}>Counted net flow</td>
                  <td style={{ padding: '0.45rem 0.5rem', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                    {money(data?.net_flow || 0, { full: true, signed: true })}
                  </td>
                  <td colSpan={3} />
                </tr>
              </tfoot>
            </table>
          )}

          {/* Month by month, because a period total hides a reversal — and on
              this page it already has. Jan-Aug 2026 agreed with Finance's
              independent build to within 20% while March disagreed by $71.7M in
              the opposite direction. */}
          {!isFetching && (data?.months || []).length > 1 && (
            <>
              <div style={{
                marginTop: '1.1rem', marginBottom: '0.3rem', fontSize: '0.78rem',
                fontWeight: 700, color: work.ink,
              }}>
                By month
                <span style={{ color: work.muted, fontWeight: 400 }}>
                  {' '}— off platform falling while on platform rises is what a
                  book being re-papered onto PCA models looks like
                </span>
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
                  <thead>
                    <tr style={{ color: work.muted, textAlign: 'left' }}>
                      <th style={{ padding: '0.3rem 0.5rem' }}>Month</th>
                      <th style={{ padding: '0.3rem 0.5rem', textAlign: 'right' }}>Off platform</th>
                      <th style={{ padding: '0.3rem 0.5rem', textAlign: 'right' }}>On platform</th>
                      <th style={{ padding: '0.3rem 0.5rem', textAlign: 'right' }}>Unknown</th>
                      <th style={{ padding: '0.3rem 0.5rem', textAlign: 'right' }}>Net</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data.months || []).map((m) => (
                      <tr key={m.period_month} style={{ borderTop: `1px solid ${work.hairline}` }}>
                        <td style={{ padding: '0.35rem 0.5rem' }}>{monthLabel(m.period_month)}</td>
                        <td style={{ padding: '0.35rem 0.5rem', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                          {money(m.off, { full: true, signed: true })}
                        </td>
                        <td style={{ padding: '0.35rem 0.5rem', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                          {money(m.on, { full: true, signed: true })}
                        </td>
                        <td style={{ padding: '0.35rem 0.5rem', textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: work.muted }}>
                          {money(m.unknown, { full: true, signed: true })}
                        </td>
                        <td style={{ padding: '0.35rem 0.5rem', textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>
                          {money(m.net_flow, { full: true, signed: true })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* What the account layer can and cannot answer, on the panel that
              depends on it. `history_since` bounds every migration claim. */}
          {!isFetching && coverage && (
            <div style={{ marginTop: '0.7rem', color: work.muted, fontSize: '0.76rem' }}>
              {(coverage.total || 0).toLocaleString()} Orion accounts held
              {' '}({(coverage.closed || 0).toLocaleString()} closed,
              {' '}{(coverage.off_platform || 0).toLocaleString()} off platform).
              {coverage.history_since ? (
                <>
                  {' '}Account history since {coverage.history_since} — platform
                  migration (off → on) becomes measurable from that date and
                  cannot be reconstructed before it.
                </>
              ) : (
                <>
                  {' '}No account history yet: run a live sync, and migration
                  becomes measurable from that day forward.
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Counted net flow by Orion management style, beside the internal journals.
 *
 * WHY THIS PANEL EXISTS, in the words of the question that produced it (Finance,
 * 2026-09-29): "when accounts first come into the Orion system, the Model Name
 * and Management style are coded as Unassigned. Once the account is sleeved,
 * those fields are then changed to Sleeved Custodial Account. Therefore, the
 * unassigned column in the sales report is basically a net of what accounts came
 * in and are still Unassigned, less the accounts that were sleeved."
 *
 * That is a real property of Orion and it is a real trap for a report. New money
 * arrives before anyone has decided where it goes; the decision arrives later and
 * re-papers the account. A report that groups RAW movements by style shows the
 * arrival as a positive and the sleeving as a negative, and the new money reads
 * as having left again. It never left.
 *
 * THE HUB'S NUMBER DOES NOT HAVE THAT BUG, AND THIS IS WHERE THAT IS VISIBLE
 * RATHER THAN PROMISED. Sleeving moves money with types 171/172, internal
 * journals, both classified `excluded` - so the arrival is counted exactly once,
 * as its type-39 merge-in or type-8 contribution, and the sleeving cannot net it
 * back out. Both lifecycle styles therefore come out POSITIVE on counted flow.
 *
 * SO THE JOURNALS GET THEIR OWN COLUMN instead of being left off. Someone who
 * has watched Unassigned net against Sleeved in another report does not need to
 * be told the netting is absent here; they need to see where it went. The column
 * is headed "not counted", sits outside the total in the footer, and firm-wide
 * the two halves cancel to a few thousand dollars on hundreds of millions - which
 * is the whole argument for excluding them, on screen.
 *
 * Bars carry polarity only, in the page's own positive/negative pair, off a
 * shared zero line. Not a share-of-total: a style's net flow can be negative, so
 * a share bar would be a lie. Identity is the row label. The journal figures stay
 * in muted text and never get a bar - they are a different measure on a different
 * scale, and giving them one would invite reading two incomparable lengths
 * against each other.
 */
function StyleSplit({ range, firm }) {
  const [open, setOpen] = useState(false);
  const { data, isFetching } = useGetAumStylesQuery(
    { ...range, firm: firm || undefined }, { skip: !open },
  );

  const rows = data?.styles || [];
  // One zero axis across every bar, scaled to the largest absolute net. A
  // per-row scale would make a $700k style look like a $22M one.
  const widest = Math.max(1, ...rows.map((r) => Math.abs(r.net_flow || 0)));
  // Same bound the platform split reports, measured the same way: gross, never
  // net. The sync only began stamping style on 2026-09-18, so a window it has
  // not re-pulled is mostly unplaced, and saying so is the difference between a
  // panel that is incomplete and one that is wrong.
  const unstampedShare = data?.unstamped_share;
  const mostlyUnstamped = typeof unstampedShare === 'number' && unstampedShare > 0.2;
  const journalNet = (data?.journal_in || 0) + (data?.journal_out || 0);
  // Nonzero only if someone has moved types 171/172 into the total on the
  // flow-types admin page, at which point this panel's "not counted" heading
  // would be false and the headline would double-count every sleeving.
  const journalsCounted = data?.journals_counted || 0;

  return (
    <div style={{
      background: work.surface, border: `1px solid ${work.hairline}`,
      borderRadius: 6, marginTop: '0.75rem',
    }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: '0.5rem',
          padding: '0.7rem 0.9rem', background: 'transparent', border: 0,
          cursor: 'pointer', color: work.ink, fontSize: '0.84rem', fontWeight: 700,
          textAlign: 'left',
        }}
      >
        <i className={`bi bi-chevron-${open ? 'down' : 'right'}`} />
        By management style
        <span style={{ color: work.muted, fontWeight: 400 }}>
          {' '}- where the counted money landed, and the sleeving that moves it after
        </span>
      </button>

      {open && (
        <div style={{ padding: '0 0.9rem 0.9rem' }}>
          {isFetching && <div style={{ color: work.muted, fontSize: '0.82rem' }}>Loading...</div>}

          {/* The panel's own point, said before the table rather than left to be
              inferred from it. */}
          {!isFetching && (
            <div style={{
              fontSize: '0.8rem', color: work.muted, marginBottom: '0.8rem',
              lineHeight: 1.45,
            }}>
              New money is coded <strong>Unassigned</strong> until someone decides
              where it goes, then <strong>Sleeved Custodial Account</strong> once
              the account is sleeved. Sleeving moves the money with internal
              journals, which this page excludes - so an arrival is counted once,
              when it arrives, and the sleeving never nets it back out. Both
              styles read positive below. A report that groups raw movements by
              style instead will show Unassigned netting against Sleeved and the
              new money looking like it left.
            </div>
          )}

          {!isFetching && journalsCounted > 0 && (
            <div style={{
              background: '#fffaf0', border: `1px solid ${work.goldenrod}`,
              borderRadius: 6, padding: '0.6rem 0.8rem', marginBottom: '0.8rem',
              fontSize: '0.8rem',
            }}>
              <i className="bi bi-exclamation-triangle-fill" style={{ color: work.goldenrod }} />
              {' '}
              <strong>
                {journalsCounted.toLocaleString()} internal journal
                {journalsCounted === 1 ? '' : 's'} are inside the total.
              </strong>
              {' '}
              Types 171/172 have been classified as flows on Super Admin - Orion
              Flow Types. Every sleeving in this period is being counted a second
              time, and the journal column below is part of the headline rather
              than beside it.
            </div>
          )}

          {!isFetching && mostlyUnstamped && (
            <div style={{
              background: '#fffaf0', border: `1px solid ${work.goldenrod}`,
              borderRadius: 6, padding: '0.6rem 0.8rem', marginBottom: '0.8rem',
              fontSize: '0.8rem',
            }}>
              <i className="bi bi-exclamation-triangle-fill" style={{ color: work.goldenrod }} />
              {' '}
              <strong>{Math.round(unstampedShare * 100)}% of this period has no style stamped on it.</strong>
              {' '}
              Management style is read from a field the sync only began storing on
              2026-09-18, so months cached before that carry none. A full live
              sync over the window fills it in; until then these rows are
              incomplete rather than wrong.
            </div>
          )}

          {!isFetching && !rows.length && (
            <div style={{ color: work.muted, fontSize: '0.82rem' }}>
              No flows in this period.
            </div>
          )}

          {!isFetching && rows.length > 0 && (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                <thead>
                  <tr style={{ color: work.muted, textAlign: 'left' }}>
                    <th style={{ padding: '0.3rem 0.5rem' }}>Management style</th>
                    <th style={{ padding: '0.3rem 0.5rem', width: '22%' }} />
                    <th style={{ padding: '0.3rem 0.5rem', textAlign: 'right' }}>Net flow</th>
                    <th style={{ padding: '0.3rem 0.5rem', textAlign: 'right' }}>In</th>
                    <th style={{ padding: '0.3rem 0.5rem', textAlign: 'right' }}>Out</th>
                    <th style={{ padding: '0.3rem 0.5rem', textAlign: 'right' }}>
                      Internal journals
                      <div style={{ fontWeight: 400, fontSize: '0.68rem' }}>not counted</div>
                    </th>
                    <th style={{ padding: '0.3rem 0.5rem', textAlign: 'right' }}>Households</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const net = r.net_flow || 0;
                    const pct = (Math.abs(net) / widest) * 50;
                    const label = r.style || 'No style stamped';
                    return (
                      <tr key={label} style={{ borderTop: `1px solid ${work.hairline}` }}>
                        <td style={{ padding: '0.45rem 0.5rem' }}>
                          <div style={{ fontWeight: 600 }}>{label}</div>
                          <div style={{ color: work.muted, fontSize: '0.72rem' }}>
                            {r.style === null && 'the sync had not stored style when these were cached'}
                            {r.folded && `${r.folded} styles, each smaller than the rows above`}
                            {!r.folded && r.style !== null && (
                              r.platform === 'off' ? 'off platform' : 'on platform'
                            )}
                          </div>
                        </td>
                        <td style={{ padding: '0.45rem 0.5rem' }}>
                          {/* A shared zero line down the middle, so a negative
                              style reads as negative rather than as a small
                              positive one. */}
                          <div
                            title={`${label}: ${money(net, { full: true, signed: true })} counted`
                              + ` (in ${money(r.inflows || 0, { full: true })},`
                              + ` out ${money(r.outflows || 0, { full: true })});`
                              + ` internal journals ${money((r.journal_in || 0) + (r.journal_out || 0), { full: true, signed: true })}`
                              + ' net, not counted'}
                            style={{
                              position: 'relative', height: 14,
                              borderLeft: `1px solid ${work.hairline}`,
                              borderRight: `1px solid ${work.hairline}`,
                            }}
                          >
                            <div style={{
                              position: 'absolute', left: '50%', top: 0, bottom: 0,
                              width: 1, background: work.hairlineStrong,
                            }} />
                            <div
                              style={{
                                position: 'absolute', top: 2, bottom: 2,
                                width: `${pct}%`,
                                left: net >= 0 ? '50%' : `${50 - pct}%`,
                                background: net >= 0 ? flowPositive : flowNegative,
                                borderRadius: net >= 0 ? '0 3px 3px 0' : '3px 0 0 3px',
                              }}
                            />
                          </div>
                        </td>
                        <td style={{
                          padding: '0.45rem 0.5rem', textAlign: 'right',
                          fontVariantNumeric: 'tabular-nums', fontWeight: 700,
                        }}>
                          {money(net, { full: true, signed: true })}
                        </td>
                        <td style={{ padding: '0.45rem 0.5rem', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                          {money(r.inflows || 0, { full: true })}
                        </td>
                        <td style={{ padding: '0.45rem 0.5rem', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                          {money(r.outflows || 0, { full: true })}
                        </td>
                        {/* Muted, and never the polarity colours: this is the
                            money the total deliberately ignores, and painting it
                            like a flow is exactly the confusion the panel is
                            here to undo. */}
                        <td style={{
                          padding: '0.45rem 0.5rem', textAlign: 'right',
                          fontVariantNumeric: 'tabular-nums', color: work.muted,
                        }}>
                          {money(r.journal_out || 0, { full: true, signed: true })}
                          {' / '}
                          {money(r.journal_in || 0, { full: true, signed: true })}
                        </td>
                        <td style={{ padding: '0.45rem 0.5rem', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                          {r.households === null ? '—' : (r.households || 0).toLocaleString()}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr style={{ borderTop: `2px solid ${work.hairlineStrong}`, fontWeight: 700 }}>
                    <td style={{ padding: '0.45rem 0.5rem' }} colSpan={2}>Counted net flow</td>
                    <td style={{ padding: '0.45rem 0.5rem', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                      {money(data?.net_flow || 0, { full: true, signed: true })}
                    </td>
                    <td colSpan={4} />
                  </tr>
                </tfoot>
              </table>
            </div>
          )}

          {/* The excluded total, stated outside the table and computed for
              whatever filter is on screen. Both halves of a journal come off one
              custodian file line, so they cancel where both halves are in view -
              which a firm filter need not guarantee, since a journal can move
              money between two offices' households. Shown as a measured figure
              the reader can check, never as a law they have to take on faith. */}
          {!isFetching && rows.length > 0 && (
            <div style={{ marginTop: '0.7rem', color: work.muted, fontSize: '0.76rem' }}>
              Internal journals in this period:
              {' '}{money(data?.journal_out || 0, { full: true, signed: true })} out
              {' '}and {money(data?.journal_in || 0, { full: true, signed: true })} in,
              {' '}netting <strong>{money(journalNet, { full: true, signed: true })}</strong>
              {' '}across what is filtered here. Both halves of a journal come off
              one custodian file line, so a net near zero is the two halves of the
              same movements - the reason they are excluded from every total on
              this page.
              {' '}{(data?.distinct_styles || 0).toLocaleString()} styles carried
              activity in this period.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function NotInFsc({ range, firm, coverage }) {
  const [open, setOpen] = useState(false);
  const { data, isFetching } = useGetAumNotInFscQuery(
    { ...range, firm: firm || undefined }, { skip: !open },
  );
  const n = coverage?.households_not_in_fsc || 0;
  if (!n) return null;
  const net = coverage?.net_flow_not_in_fsc || 0;

  return (
    <div style={{
      background: '#fffaf0', border: `1px solid ${work.goldenrod}`,
      borderRadius: 6, padding: '0.7rem 0.9rem', marginBottom: '1rem',
      fontSize: '0.82rem', color: work.ink,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
        <i className="bi bi-exclamation-triangle-fill" style={{ color: work.goldenrod }} />
        <strong>{n.toLocaleString()} household{n === 1 ? '' : 's'} not in Salesforce</strong>
        <span style={{ color: work.muted }}>
          still included in the totals ({money(net, { signed: true })} net)
        </span>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          style={{
            marginLeft: 'auto', padding: '0.2rem 0.6rem', fontSize: '0.78rem',
            fontWeight: 600, borderRadius: 4, cursor: 'pointer',
            border: `1px solid ${work.goldenrod}`, background: 'transparent',
            color: work.ink,
          }}
        >
          {open ? 'Hide' : 'Show these'}
        </button>
      </div>

      {open && (
        <div style={{ marginTop: '0.7rem', overflowX: 'auto' }}>
          {isFetching && <span style={{ color: work.muted }}>Loading…</span>}
          {!isFetching && (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
              <thead>
                <tr style={{ color: work.muted, textAlign: 'left' }}>
                  <th style={{ padding: '0.3rem 0.5rem' }}>Household</th>
                  <th style={{ padding: '0.3rem 0.5rem' }}>Office</th>
                  <th style={{ padding: '0.3rem 0.5rem' }}>Advisor</th>
                  <th style={{ padding: '0.3rem 0.5rem', textAlign: 'right' }}>Net flow</th>
                  <th style={{ padding: '0.3rem 0.5rem', textAlign: 'right' }}>Current AUM</th>
                </tr>
              </thead>
              <tbody>
                {(data?.households || []).map((h) => (
                  <tr key={h.orion_household_id} style={{ borderTop: `1px solid ${work.hairline}` }}>
                    <td style={{ padding: '0.3rem 0.5rem' }}>{h.name || h.orion_household_id}</td>
                    <td style={{ padding: '0.3rem 0.5rem' }}>{h.office_name}</td>
                    <td style={{ padding: '0.3rem 0.5rem' }}>{h.representative_name || '—'}</td>
                    <td style={{ padding: '0.3rem 0.5rem', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                      {money(h.net_flow, { full: true, signed: true })}
                    </td>
                    <td style={{ padding: '0.3rem 0.5rem', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                      {money(h.current_aum, { full: true })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {/* A capped list must never read as the whole problem. */}
          {data?.truncated && (
            <div style={{ marginTop: '0.4rem', color: work.muted }}>
              Showing the largest {data.count} only — there are more.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * "How this number is built" — every Orion transaction type we counted, and
 * every one we left out, priced.
 *
 * Collapsed by default and fetched only when opened. It exists because the first
 * question anyone asks when this figure differs from their own report is "what
 * did you include?", and the honest answer moves the conversation from "your
 * number is wrong" to "we disagree about management fees" — which is a decision
 * someone can actually make.
 */
function Breakdown({ range, firm }) {
  const [open, setOpen] = useState(false);
  const { data, isFetching } = useGetAumBreakdownQuery(
    { ...range, firm: firm || undefined }, { skip: !open },
  );
  const types = data?.types;

  const section = (rows, title, note) => {
    if (!rows?.length) return null;
    return (
      <div style={{ marginBottom: '1rem' }}>
        <div style={{
          fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase',
          letterSpacing: '1px', color: work.muted, marginBottom: 4,
        }}>
          {title}
        </div>
        {note && (
          <div style={{ fontSize: '0.78rem', color: work.muted, marginBottom: 6 }}>{note}</div>
        )}
        <table style={{ width: '100%', fontSize: '0.82rem', borderCollapse: 'collapse' }}>
          <tbody>
            {rows.map((r) => (
              <tr key={`${r.type_id}-${r.type_name}`} style={{ borderTop: `1px solid ${work.hairline}` }}>
                <td style={{ padding: '0.35rem 0.5rem', width: 60, color: work.muted }}>
                  {r.type_id}
                </td>
                <td style={{ padding: '0.35rem 0.5rem' }}>
                  {r.type_name || '—'}
                  {r.reason && (
                    <div style={{ color: work.muted, fontSize: '0.74rem' }}>{r.reason}</div>
                  )}
                </td>
                <td style={{ padding: '0.35rem 0.5rem', textAlign: 'right', color: work.muted,
                             fontVariantNumeric: 'tabular-nums' }}>
                  {r.transactions.toLocaleString()} 
                </td>
                <td style={{ padding: '0.35rem 0.5rem', textAlign: 'right', fontWeight: 600,
                             fontVariantNumeric: 'tabular-nums' }}>
                  {money(r.amount, { full: true })}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div style={{
      background: work.surface, border: `1px solid ${work.hairline}`,
      borderRadius: 6, marginTop: '0.75rem',
    }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={{
          width: '100%', textAlign: 'left', background: 'none', border: 'none',
          padding: '0.75rem 1.1rem', cursor: 'pointer', color: work.ink,
          fontWeight: 700, fontSize: '0.9rem',
        }}
      >
        <i className={`bi bi-chevron-${open ? 'down' : 'right'}`}
           style={{ fontSize: '0.75rem', marginRight: 6, color: work.muted }} />
        How the numbers are calculated 
        <span style={{ color: work.muted, fontWeight: 400, fontSize: '0.8rem', marginLeft: 8 }}>
        </span>
      </button>

      {open && (
        <div style={{ padding: '0 1.1rem 1.1rem' }}>
          {isFetching && <div style={{ color: work.muted, fontSize: '0.85rem' }}>Loading…</div>}
          {types && (
            <>
              {section(types.inflow, 'Counted as inflows')}
              {section(types.outflow, 'Counted as outflows')}
              {section(types.excluded, 'Excluded',
                'These are in Orion but not in the total above. The amount is what '
                + 'each would move the headline by if we counted it.')}
              {section(types.unclassified, 'Unclassified',
                'Transaction types nobody has made a decision about yet. They are in '
                + 'no total — if this section is not empty, the headline is incomplete.')}

              <div style={{
                borderTop: `1px solid ${work.hairlineStrong}`, paddingTop: '0.75rem',
                fontSize: '0.82rem', color: work.muted,
              }}>
                <div style={{
                  fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase',
                  letterSpacing: '1px', marginBottom: 6,
                }}>
                  Coverage
                </div>
                {/* Orion's book and Salesforce's disagree about who owns what.
                    A total can be short for that reason alone, with nothing
                    wrong in how transactions were classified. */}
                {data.book && (
                  <div>
                    Orion holds {Number(data.book.orion_households || 0).toLocaleString()} households;
                    Salesforce holds {Number(data.book.fsc_households || 0).toLocaleString()}.
                    {data.book.in_orion_only > 0 && (
                      <strong style={{ color: work.goldenrod }}>
                        {' '}{Number(data.book.in_orion_only).toLocaleString()} are in Orion only,
                        so their flows reach no firm&apos;s column.
                      </strong>
                    )}
                  </div>
                )}
                {data.exclusions?.households > 0 && (
                  <div style={{ marginTop: 4 }}>
                    {data.exclusions.rules} exclusion rule{data.exclusions.rules === 1 ? '' : 's'} in
                    force, removing {data.exclusions.households} household
                    {data.exclusions.households === 1 ? '' : 's'} (demo and test books, and
                    PCA&apos;s own corporate account).
                  </div>
                )}
                {data.exclusions?.styles > 0 && (
                  <div style={{ marginTop: 4 }}>
                    Accounts managed as {data.exclusions.style_names} are left out
                    (securities-based lending, not client money):{' '}
                    {Number(data.exclusions.style_transactions || 0).toLocaleString()} transaction
                    {data.exclusions.style_transactions === 1 ? '' : 's'},{' '}
                    {data.exclusions.style_transactions_amount < 0 ? '-' : ''}$
                    {Math.abs(Math.round(data.exclusions.style_transactions_amount || 0)).toLocaleString()}.
                  </div>
                )}
                {data.coverage?.unmatched_orion_households > 0 && (
                  <div style={{ marginTop: 4 }}>
                    {Number(data.coverage.unmatched_orion_households).toLocaleString()} Orion
                    households match no Salesforce record.
                  </div>
                )}
                <div style={{ marginTop: 8, fontStyle: 'italic' }}>
                  Off-platform assets (AssetMark and similar) are not in Orion&apos;s
                  transaction feed and are not represented here at all.
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

/** The advisor breakdown inside one office, fetched only when a row expands.
 *
 * Each advisor expands again, into their households — see `HouseholdRows`. Two
 * levels because that is the shape of the question: a firm total someone
 * disputes leads to an advisor, and the advisor leads to the household they can
 * actually check.
 */
function AdvisorRows({ officeId, range, numStyle, hasAum, hasCurrentAum }) {
  const { data, isFetching } = useGetAumFirmAdvisorsQuery({ officeId, ...range });
  const [openAdvisor, setOpenAdvisor] = useState(null);
  const span = (hasAum ? 7 : 5) + (hasCurrentAum ? 1 : 0);
  if (isFetching) {
    return (
      <tr>
        <td colSpan={span}
            style={{ padding: '0.6rem 2rem', color: work.muted, fontSize: '0.8rem' }}>
          Loading advisors…
        </td>
      </tr>
    );
  }
  return (data?.rows || []).map((a) => (
    <React.Fragment key={a.group_id}>
      <tr
        onClick={() => setOpenAdvisor((v) => (v === a.group_id ? null : a.group_id))}
        style={{ background: work.canvas, fontSize: '0.82rem', cursor: 'pointer' }}
      >
        <td style={{ padding: '0.4rem 0.6rem 0.4rem 2rem', color: work.muted }}>
          <i className={`bi bi-chevron-${openAdvisor === a.group_id ? 'down' : 'right'}`}
             style={{ fontSize: '0.65rem', marginRight: 6 }} />
          {a.group_name || '—'}
        </td>
        <td style={{ ...numStyle, fontWeight: 600 }}>{money(a.net_flow, { full: true, signed: true })}</td>
        <td style={numStyle}>{money(a.inflows, { full: true })}</td>
        <td style={numStyle}>{money(a.outflows, { full: true })}</td>
        {hasAum && <td style={numStyle}>{money(a.aum_delta, { full: true, signed: true })}</td>}
        {hasAum && <td style={numStyle}>{money(a.ending_aum, { full: true })}</td>}
        {hasCurrentAum && <td style={numStyle}>{money(a.current_aum, { full: true })}</td>}
        <td style={numStyle}>{a.households}</td>
      </tr>
      {openAdvisor === a.group_id && (
        <HouseholdRows advisor={a.group_id} firm={officeId} range={range} span={span} />
      )}
    </React.Fragment>
  ));
}

/**
 * One advisor's households: what we counted, beside what we left out.
 *
 * This is the view an advisor reconciles against, so it does not hide the
 * exclusions — a household reading $0 counted with $4.3M in "merge in" is not a
 * blank row, it is the single most interesting row on the page, and the reader
 * has to be able to see that without being told.
 *
 * MERGE IN vs CLIENT SINCE is the pairing that matters. PCA uses sleeved
 * accounts as standard and Orion models every sleeve as its own account, so a
 * new client's transferred-in book arrives as a merge rather than a
 * contribution. On a household that started this period, that number is new
 * money we are not yet counting; on an old one it is an account being
 * restructured. Nobody can judge that from a total, and everybody who knows the
 * client can judge it from this row.
 */
function HouseholdRows({ advisor, firm, range, span }) {
  const { data, isFetching } = useGetAumHouseholdsQuery({ ...range, firm, advisor });
  // The household whose Orion records are open. One at a time: this is a live
  // Orion read, and it is a reconciliation step rather than something to leave
  // several of on screen.
  const [drilldown, setDrilldown] = useState(null);
  if (isFetching) {
    return (
      <tr>
        <td colSpan={span} style={{ padding: '0.6rem 3rem', color: work.muted, fontSize: '0.78rem' }}>
          Loading households…
        </td>
      </tr>
    );
  }
  const rows = data?.rows || [];
  if (!rows.length) {
    return (
      <tr>
        <td colSpan={span} style={{ padding: '0.6rem 3rem', color: work.muted, fontSize: '0.78rem' }}>
          No household activity in this period.
        </td>
      </tr>
    );
  }
  const cell = { padding: '0.3rem 0.5rem', textAlign: 'right', fontVariantNumeric: 'tabular-nums' };
  const anyExcluded = rows.some((r) => r.excluded_total);
  return (
    <tr>
      <td colSpan={span} style={{ padding: '0.4rem 0.6rem 0.8rem 3rem', background: work.canvas }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.76rem' }}>
            <thead>
              <tr style={{ color: work.muted, textAlign: 'left' }}>
                <th style={{ padding: '0.3rem 0.5rem' }}>Household</th>
                <th style={{ padding: '0.3rem 0.5rem' }}>Client since</th>
                <th style={{ ...cell, fontWeight: 600 }}>Counted net</th>
                {/* Excluded columns are visually set apart, not hidden: they are
                    not part of the total and must not read as though they are. */}
                {anyExcluded && <th style={{ ...cell, color: work.goldenrod }}>Merge in</th>}
                {anyExcluded && <th style={{ ...cell, color: work.goldenrod }}>Merge out</th>}
                {anyExcluded && <th style={{ ...cell, color: work.goldenrod }}>Journals</th>}
                {anyExcluded && <th style={{ ...cell, color: work.goldenrod }}>Tax</th>}
                <th style={cell}>Current AUM</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.orion_household_id} style={{ borderTop: `1px solid ${work.hairline}` }}>
                  <td style={{ padding: '0.3rem 0.5rem' }}>
                    {/* The name is the drill-down. Any number on this row that
                        looks wrong is answered by what Orion actually holds, so
                        that is one click from the row rather than a separate
                        page someone has to know exists. */}
                    <button
                      type="button"
                      onClick={() => setDrilldown(r)}
                      title="Show this household's Orion transactions"
                      style={{
                        border: 0, background: 'none', padding: 0, textAlign: 'left',
                        color: work.tealBlue, textDecoration: 'underline',
                        textDecorationStyle: 'dotted', cursor: 'pointer', font: 'inherit',
                      }}
                    >
                      {r.name || r.orion_household_id}
                    </button>
                    {!r.in_fsc && (
                      <span title="No Salesforce record for this household"
                            style={{ marginLeft: 5, color: work.goldenrod, fontWeight: 700 }}>
                        not in FSC
                      </span>
                    )}
                  </td>
                  <td style={{ padding: '0.3rem 0.5rem', color: work.muted }}>
                    {r.client_start_date || '—'}
                    {r.is_new_client && (
                      <span style={{
                        marginLeft: 5, padding: '0.02rem 0.3rem', borderRadius: 3,
                        fontSize: '0.66rem', fontWeight: 700,
                        background: '#e8f3fb', color: work.tealBlue,
                      }}>
                        new
                      </span>
                    )}
                  </td>
                  <td style={{ ...cell, fontWeight: 600 }}>
                    {money(r.counted_net, { full: true, signed: true })}
                  </td>
                  {anyExcluded && <td style={cell}>{r.merge_in ? money(r.merge_in, { full: true, signed: true }) : '—'}</td>}
                  {anyExcluded && <td style={cell}>{r.merge_out ? money(r.merge_out, { full: true, signed: true }) : '—'}</td>}
                  {anyExcluded && <td style={cell}>{r.journals ? money(r.journals, { full: true, signed: true }) : '—'}</td>}
                  {anyExcluded && <td style={cell}>{r.tax ? money(r.tax, { full: true, signed: true }) : '—'}</td>}
                  <td style={cell}>{money(r.current_aum, { full: true })}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {anyExcluded && (
          <div style={{ marginTop: '0.4rem', fontSize: '0.72rem', color: work.muted }}>
            Amber columns are <strong>not</strong> in the counted net. A merge into a
            household marked <em>new</em> is that client&apos;s book arriving — see
            docs/net-new-aum.md.
          </div>
        )}
        <DownloadCsv range={range} firm={firm} advisor={advisor} />
        {drilldown && (
          <HouseholdOrionModal
            household={drilldown}
            range={range}
            onClose={() => setDrilldown(null)}
          />
        )}
      </td>
    </tr>
  );
}

/* ── The household drill-down ───────────────────────────────────────────────
 *
 * What Orion actually holds for one household, beside what our rules did with
 * it. Live from Orion, not from our cache, and the reason is the whole point of
 * the view: the sync keeps 12 of Orion's 51 fields and only fetches the ~21
 * types already on our lists, so the cache can show our classification but
 * cannot audit it. The evidence lives in the part we drop.
 *
 * THE UNPAIRED COLUMN IS THE ANSWER. "Paired transfer, nets to zero" is the
 * justification for excluding journals and merges; Orion's own
 * `transactionLinkCode` lets that be checked per household rather than assumed.
 * Christopher & Elisa Lepre, Apr–Sep 2026: journals 37 of 39 rows paired and
 * netting to ±$37.1M — plumbing, correctly excluded. Merge-in, 0 of 30 rows
 * paired, +$23.5M — a new client's book arriving by ACAT, excluded to $0. The
 * aggregate cannot tell those apart and this column can.
 */
const TREATMENTS = {
  counted_inflow:  { label: 'Counted — inflow',  fg: '#0069a6', bg: '#e8f3fb' },
  counted_outflow: { label: 'Counted — outflow', fg: '#0069a6', bg: '#e8f3fb' },
  excluded:        { label: 'Excluded',          fg: '#8a6412', bg: '#fdf4e3' },
  // Per-row, so this badge deliberately does not claim the type is counted or
  // excluded — the Counterparty split on the same row says which dollars are in.
  counted_if_external: {
    label: 'Counted if external', fg: '#0069a6', bg: '#e8f3fb',
    hint: 'Counted per transaction, not per type: only the rows whose counterparty '
        + 'account is not one of ours reach the total. The split is on this row.',
  },
  counted_if_new_client: {
    label: 'Counted if new client', fg: '#0069a6', bg: '#e8f3fb',
    hint: 'Counted per transaction, not per type: only a Starting Value on a household '
        + 'with no earlier activity in Orion reaches the total.',
  },
  // Visually distinct from "excluded" because it is a different failure: nobody
  // decided this, the type is simply on no list, so the sync never asks Orion
  // for it and it appears nowhere else in the product.
  //
  // "Not synced", NOT "not pulled" — this view pulls the records live, so a
  // badge reading "not pulled" on a row of visible Orion data reads as a
  // contradiction. What is missing is the nightly sync, not the data on screen.
  not_synced: {
    label: 'Not synced',
    fg: '#8a3b3b',
    bg: '#fbeceb',
    hint: 'The nightly sync never fetches this type, so it is in no total and not in '
        + 'our database — it is visible here only because this view reads Orion directly.',
  },
};

function TreatmentBadge({ treatment }) {
  const t = TREATMENTS[treatment] || { label: treatment, fg: work.muted, bg: work.canvas };
  return (
    <span
      title={t.hint || undefined}
      style={{
        padding: '0.05rem 0.35rem', borderRadius: 3, fontSize: '0.68rem',
        fontWeight: 700, whiteSpace: 'nowrap', color: t.fg, background: t.bg,
        cursor: t.hint ? 'help' : undefined,
      }}
    >
      {t.label}
    </span>
  );
}
TreatmentBadge.propTypes = { treatment: PropTypes.string };

function HouseholdOrionModal({ household, range, onClose }) {
  const { data, isFetching, error } = useGetAumHouseholdOrionQuery({
    household_id: household.orion_household_id, ...range,
  });
  const [openType, setOpenType] = useState(null);

  const cell = { padding: '0.3rem 0.5rem', textAlign: 'right', fontVariantNumeric: 'tabular-nums' };
  const hh = data?.household;

  return (
    <div
      role="presentation"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 1080, background: 'rgba(10,41,71,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1.2rem',
      }}
    >
      <div
        role="presentation"
        onClick={(e) => e.stopPropagation()}
        style={{
          background: work.surface, borderRadius: 6, width: 'min(1150px, 100%)',
          maxHeight: '100%', display: 'flex', flexDirection: 'column',
          boxShadow: '0 8px 30px rgba(0,0,0,.3)',
        }}
      >
        <div style={{
          display: 'flex', alignItems: 'flex-start', gap: '0.6rem', flexShrink: 0,
          padding: '0.7rem 0.9rem', borderBottom: `1px solid ${work.hairline}`,
        }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 600, color: work.ink }}>
              {household.name || household.orion_household_id}
            </div>
            <div style={{ fontSize: '0.74rem', color: work.muted, marginTop: 2 }}>
              {hh ? (
                <>
                  {hh.office_name} · {hh.representative_name || 'no rep'} · client since{' '}
                  {hh.client_start_date || '—'} · current AUM {money(hh.current_aum, { full: true })}
                  {' · '}Orion client {hh.orion_client_id}
                </>
              ) : household.orion_household_id}
            </div>
          </div>
          <button type="button" onClick={onClose} className="btn btn-sm btn-outline-secondary">
            <i className="bi bi-x-lg" />
          </button>
        </div>

        <div style={{ overflowY: 'auto', padding: '0.7rem 0.9rem' }}>
          {/* The period the SERVER resolved, not the one this component was
              handed. `range.end` is a month START — the periods on this page are
              whole months and the backend widens it to the month end before
              asking Orion, so echoing the prop here would understate the window
              by a month on a screen used to reconcile against it. */}
          <div style={{ fontSize: '0.74rem', color: work.muted, marginBottom: '0.6rem' }}>
            Read live from Orion for {data?.start || range.start} to {data?.end || range.end}
            {' '}— every transaction type Orion holds for this household
          </div>

          {isFetching && (
            <div style={{ padding: '1.5rem', textAlign: 'center', color: work.muted, fontSize: '0.8rem' }}>
              <span className="spinner-border spinner-border-sm me-2" />
              Reading Orion…
            </div>
          )}
          {error && (
            <div style={{ padding: '0.8rem', color: '#8a3b3b', background: '#fbeceb',
                          borderRadius: 4, fontSize: '0.8rem' }}>
              {error?.data?.detail || 'Could not read this household from Orion.'}
            </div>
          )}

          {data && !isFetching && (
            <>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.7rem' }}>
                <Tally label="Counted net" value={data.totals.counted_net} strong />
                <Tally label="Excluded" value={data.totals.excluded_net} tone={work.goldenrod} />
                <Tally label="Not synced" value={data.totals.not_synced_net} tone="#8a3b3b" />
                <Tally label="Transactions" value={data.transactions} count />
              </div>

              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.76rem' }}>
                <thead>
                  <tr style={{ color: work.muted, textAlign: 'left' }}>
                    <th style={{ padding: '0.3rem 0.5rem' }}>Orion type</th>
                    <th style={{ padding: '0.3rem 0.5rem' }}>Our treatment</th>
                    <th style={cell}>Transactions</th>
                    <th style={cell}>Total</th>
                    <th style={cell} title="No other transaction in this household shares Orion's link code">
                      Unpaired
                    </th>
                    <th style={cell}>Paired</th>
                  </tr>
                </thead>
                <tbody>
                  {data.types.map((t) => (
                    <React.Fragment key={t.trans_type_id}>
                      <tr
                        onClick={() => setOpenType(openType === t.trans_type_id ? null : t.trans_type_id)}
                        style={{
                          borderTop: `1px solid ${work.hairline}`, cursor: 'pointer',
                          background: openType === t.trans_type_id ? work.canvas : 'transparent',
                        }}
                      >
                        <td style={{ padding: '0.3rem 0.5rem' }}>
                          <i className={`bi bi-chevron-${openType === t.trans_type_id ? 'down' : 'right'}`}
                             style={{ fontSize: '0.6rem', color: work.muted, marginRight: 5 }} />
                          <strong>{t.trans_type_id}</strong> {t.trans_type_name}
                          {t.type_code && <span style={{ color: work.muted }}> ({t.type_code})</span>}
                        </td>
                        <td style={{ padding: '0.3rem 0.5rem' }}>
                          <TreatmentBadge treatment={t.treatment} />
                        </td>
                        <td style={cell}>{t.transactions.toLocaleString()}</td>
                        <td style={{ ...cell, fontWeight: 600 }}>
                          {money(t.total, { full: true, signed: true })}
                        </td>
                        <td style={{ ...cell, color: t.unpaired_total ? work.ink : work.muted }}>
                          {t.unpaired_total ? money(t.unpaired_total, { full: true, signed: true }) : '—'}
                        </td>
                        <td style={{ ...cell, color: work.muted }}>
                          {t.paired_transactions}/{t.transactions}
                        </td>
                      </tr>
                      {t.reason && (
                        <tr style={{ background: openType === t.trans_type_id ? work.canvas : 'transparent' }}>
                          <td colSpan={6} style={{ padding: '0 0.5rem 0.3rem 1.6rem',
                                                   fontSize: '0.71rem', color: work.muted }}>
                            {t.reason}
                          </td>
                        </tr>
                      )}
                      {/* Merges only: was the counterparty account one of ours?
                          This is the line the merge decision turns on, so it sits
                          on the summary row rather than only inside the expansion. */}
                      {t.is_merge && (
                        <tr style={{ background: openType === t.trans_type_id ? work.canvas : 'transparent' }}>
                          <td colSpan={6} style={{ padding: '0 0.5rem 0.35rem 1.6rem',
                                                   fontSize: '0.71rem' }}>
                            <SourceSplit type={t} />
                          </td>
                        </tr>
                      )}
                      {openType === t.trans_type_id && (
                        <TypeRows
                          householdId={household.orion_household_id}
                          range={range}
                          typeId={t.trans_type_id}
                          isMerge={!!t.is_merge}
                        />
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>

              {/* Scoped deliberately narrowly. Measured 2026-09-09: across 13
                  months, ZERO of 24,814 type-39 merge-ins share a link code with
                  any merge-out firm-wide — the code is a per-row file-line id,
                  not a counterparty key. So it pairs journals (both halves come
                  off one line) and is vacuous for merges, and this note must not
                  invite anyone to read a merge's 0-of-30 as evidence. */}
              <div style={{ marginTop: '0.6rem', fontSize: '0.72rem', color: work.muted }}>
                <strong>Unpaired</strong> means no other transaction in this household and
                period shares Orion&apos;s <code>transactionLinkCode</code>. That identifies
                two halves generated from one custodian file line, which is conclusive for{' '}
                <strong>journals</strong> — paired journal dollars are plumbing. It does{' '}
                <strong>not</strong> work for merge types: a merge-in never shares a line
                with a merge-out, so it always reads unpaired. For a merge, the evidence is
                the file code and notes on the rows below —{' '}
                <code>TOA</code>/&ldquo;ACAT RECEIVE&rdquo; is a transfer in from another
                firm, <code>JNL</code> is an internal journal.
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
HouseholdOrionModal.propTypes = {
  household: PropTypes.shape({
    orion_household_id: PropTypes.string.isRequired,
    name: PropTypes.string,
  }).isRequired,
  range: PropTypes.shape({ start: PropTypes.string, end: PropTypes.string }).isRequired,
  onClose: PropTypes.func.isRequired,
};

function Tally({ label, value, strong, tone, count }) {
  return (
    <div style={{
      border: `1px solid ${work.hairline}`, borderRadius: 4, padding: '0.35rem 0.6rem',
      minWidth: 120, background: work.surface,
    }}>
      <div style={{ fontSize: '0.68rem', color: work.muted, textTransform: 'uppercase',
                    letterSpacing: '0.02em' }}>
        {label}
      </div>
      <div style={{
        fontSize: '0.95rem', fontWeight: strong ? 700 : 600,
        color: tone || work.ink, fontVariantNumeric: 'tabular-nums',
      }}>
        {count ? Number(value || 0).toLocaleString() : money(value, { full: true, signed: !count })}
      </div>
    </div>
  );
}
Tally.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.number,
  strong: PropTypes.bool,
  tone: PropTypes.string,
  count: PropTypes.bool,
};

/* Where a merge's money actually came from, split three ways.
 *
 * THE TEST THAT MATTERS, and it is not the link code. Two independent signals,
 * measured against each other on 24,814 type-39 rows over 13 months:
 *
 *   - a matching merge-OUT of the same date and amount anywhere at PCA, which is
 *     direct evidence the counterparty account is ours;
 *   - the custodian's own file transaction code — TOA is a transfer of assets
 *     (an ACAT, which by definition crosses two different broker-dealers), JNL
 *     is a journal.
 *
 * They agree: TOA-coded rows have a PCA counterpart 0.9% of the time, JNL-coded
 * 41.6%. `unclear` is left genuinely unclear rather than guessed, because a
 * wrong "external" invents net new assets — TFR/DRS, $64M firm-wide, sit there.
 */
const SOURCES = {
  external: { label: 'External', fg: '#1a7f4e', bg: '#eaf6ef',
              hint: 'No matching merge-out anywhere at PCA, and the custodian coded it as '
                  + 'a transfer in from another firm. This is a client’s assets arriving.' },
  internal: { label: 'Internal', fg: '#6b7280', bg: '#eef1f5',
              hint: 'Either the matching merge-out sits in a PCA account, or the custodian '
                  + 'coded it as a journal. Assets moved inside the firm.' },
  unclear:  { label: 'Unclear',  fg: '#8a6412', bg: '#fdf4e3',
              hint: 'The custodian code is used for both internal and external moves and no '
                  + 'matching merge-out was found. Needs a look at the account.' },
};

function SourceSplit({ type }) {
  const parts = [
    ['external', type.external_total],
    ['internal', type.internal_total],
    ['unclear', type.unclear_total],
  ].filter(([, v]) => v);
  if (!parts.length) return null;
  return (
    <span style={{ display: 'inline-flex', gap: '0.6rem', flexWrap: 'wrap', alignItems: 'center' }}>
      <span style={{ color: work.muted }}>Counterparty account:</span>
      {parts.map(([key, value]) => (
        <span key={key} title={SOURCES[key].hint}
              style={{ cursor: 'help', color: SOURCES[key].fg, fontWeight: 600 }}>
          <span style={{
            padding: '0.02rem 0.3rem', borderRadius: 3, fontSize: '0.66rem',
            fontWeight: 700, background: SOURCES[key].bg, marginRight: 4,
          }}>
            {SOURCES[key].label}
          </span>
          {money(value, { full: true, signed: true })}
        </span>
      ))}
    </span>
  );
}
SourceSplit.propTypes = {
  type: PropTypes.shape({
    external_total: PropTypes.number,
    internal_total: PropTypes.number,
    unclear_total: PropTypes.number,
  }).isRequired,
};

/* One type's actual Orion records, fetched only when that type is opened.
 *
 * A second Orion call rather than shipping 51 fields of every row up front —
 * Lepre alone is ~11k transactions in a six-month window, and `transTypeId`
 * narrows it server-side, so the expensive fields are only ever fetched for the
 * handful of types someone is actually questioning.
 *
 * The four columns after the amount are the ones our own table does not store,
 * and they are the reason this view exists. */
function TypeRows({ householdId, range, typeId, isMerge }) {
  const { data, isFetching, error } = useGetAumHouseholdOrionQuery({
    household_id: householdId, ...range, type_id: typeId,
  });
  const rows = data?.rows || [];
  const sub = { padding: '0.25rem 0.45rem', verticalAlign: 'top' };

  return (
    <tr>
      <td colSpan={6} style={{ padding: '0.3rem 0.5rem 0.7rem 1.6rem', background: work.canvas }}>
        {isFetching && (
          <div style={{ color: work.muted, fontSize: '0.74rem' }}>
            <span className="spinner-border spinner-border-sm me-2" />Reading Orion…
          </div>
        )}
        {error && (
          <div style={{ color: '#8a3b3b', fontSize: '0.74rem' }}>
            {error?.data?.detail || 'Could not read these transactions from Orion.'}
          </div>
        )}
        {!isFetching && !error && (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.72rem' }}>
              <thead>
                <tr style={{ color: work.muted, textAlign: 'left' }}>
                  <th style={sub}>Date</th>
                  <th style={{ ...sub, textAlign: 'right' }}>Amount</th>
                  {/* Merges only. On every other type the question does not
                      arise, and an empty column would imply it does. */}
                  {isMerge && (
                    <th style={sub} title="Whether the account on the other side of this
                                           move is one of ours">
                      Counterparty
                    </th>
                  )}
                  <th style={sub}>Account</th>
                  <th style={sub}>Product</th>
                  <th style={sub} title="Orion's file transaction code — 10TOA+ is a transfer of assets">
                    File code
                  </th>
                  <th style={sub}>Link code</th>
                  <th style={sub}>Created by</th>
                  <th style={sub}>Custodian notes</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} style={{ borderTop: `1px solid ${work.hairline}` }}>
                    <td style={{ ...sub, whiteSpace: 'nowrap' }}>{String(r.transDate || '').slice(0, 10)}</td>
                    <td style={{ ...sub, textAlign: 'right', fontVariantNumeric: 'tabular-nums',
                                 fontWeight: 600, whiteSpace: 'nowrap' }}>
                      {money(r.transAmount, { full: true, signed: true })}
                    </td>
                    {isMerge && (
                      <td style={{ ...sub, whiteSpace: 'nowrap' }}>
                        {/* The basis is the tooltip because "External" on its own
                            is an assertion, and this column exists to be argued
                            with. */}
                        <span
                          title={r._source_basis || undefined}
                          style={{
                            padding: '0.02rem 0.3rem', borderRadius: 3, fontSize: '0.66rem',
                            fontWeight: 700, cursor: r._source_basis ? 'help' : undefined,
                            color: (SOURCES[r._source] || {}).fg,
                            background: (SOURCES[r._source] || {}).bg,
                          }}
                        >
                          {(SOURCES[r._source] || {}).label || '—'}
                        </span>
                      </td>
                    )}
                    <td style={{ ...sub, whiteSpace: 'nowrap' }}>
                      {r.accountNumber || '—'}
                      {r.accountType && <span style={{ color: work.muted }}> ({r.accountType})</span>}
                    </td>
                    <td style={sub}>{r.productName || '—'}</td>
                    <td style={{ ...sub, whiteSpace: 'nowrap', fontWeight: 600 }}>
                      {r._file_code || '—'}
                    </td>
                    <td style={{ ...sub, whiteSpace: 'nowrap' }}>
                      {r.transactionLinkCode || '—'}
                      {/* The paired/unpaired verdict is suppressed on merge rows:
                          a merge-in never shares a file line with a merge-out, so
                          it would read "unpaired" on every row and mean nothing.
                          The Counterparty column is the real answer there. */}
                      {!isMerge && (
                        <span style={{
                          marginLeft: 4, fontWeight: 700,
                          color: r._paired ? work.muted : work.goldenrod,
                        }}>
                          {r._paired ? 'paired' : 'unpaired'}
                        </span>
                      )}
                    </td>
                    <td style={sub}>{r.createdBy || '—'}</td>
                    <td style={{ ...sub, minWidth: 220 }}>{r.notes || '—'}</td>
                  </tr>
                ))}
                {!rows.length && (
                  <tr>
                    <td colSpan={isMerge ? 9 : 8} style={{ ...sub, color: work.muted }}>
                      Orion returned no rows for this type in this period.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            {data?.rows_truncated && (
              <div style={{ marginTop: '0.3rem', fontSize: '0.7rem', color: work.muted }}>
                Showing the first 500 rows of this type. The totals above cover all of them.
              </div>
            )}
          </div>
        )}
      </td>
    </tr>
  );
}
TypeRows.propTypes = {
  householdId: PropTypes.string.isRequired,
  range: PropTypes.shape({ start: PropTypes.string, end: PropTypes.string }).isRequired,
  typeId: PropTypes.number.isRequired,
  isMerge: PropTypes.bool,
};

/** Download the same rows as CSV, because this is what gets pivoted.
 *
 * Fetched through the RTK client rather than a plain link so the request
 * carries the session's bearer token — a bare <a href> would 401. */
function DownloadCsv({ range, firm, advisor }) {
  const [fetchCsv, { isLoading }] = useLazyGetAumHouseholdsCsvQuery();
  const [error, setError] = useState(null);

  async function go() {
    setError(null);
    const res = await fetchCsv({ ...range, firm, advisor });
    if (res.error || !res.data) {
      setError('Could not build the download.');
      return;
    }
    const url = URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = `net-new-aum-${(advisor || firm || 'all').replace(/\s+/g, '-')}-${range.start}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    // Released on the next tick — revoking synchronously can cancel the
    // download in Safari before it has read the blob.
    setTimeout(() => URL.revokeObjectURL(url), 0);
  }

  return (
    <div style={{ marginTop: '0.5rem' }}>
      <button
        type="button"
        onClick={go}
        disabled={isLoading}
        style={{
          padding: '0.25rem 0.6rem', fontSize: '0.74rem', fontWeight: 600,
          borderRadius: 4, cursor: isLoading ? 'default' : 'pointer',
          border: `1px solid ${work.hairlineStrong}`, background: work.surface,
          color: work.ink,
        }}
      >
        <i className="bi bi-download" /> {isLoading ? 'Building…' : 'Download CSV'}
      </button>
      {error && (
        <span style={{ marginLeft: 8, color: flowNegative, fontSize: '0.74rem' }}>{error}</span>
      )}
    </div>
  );
}

// ── Prop contracts ──────────────────────────────────────────────────────────
// Declared for every component in this file, matching the convention in
// routes/YourDay.js and components/DocumentBrowser.jsx. `react/prop-types` is an
// ERROR under .eslintrc.js, so omitting these does not merely warn — it fails
// the dev-server compile and the page does not serve at all.

// A whole-month period, as the API takes it: 'YYYY-MM-DD' on the 1st.
const rangeShape = PropTypes.shape({
  start: PropTypes.string,
  end: PropTypes.string,
});

StatTile.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node,
  delta: PropTypes.number,
  deltaLabel: PropTypes.string,
  note: PropTypes.string,
  hero: PropTypes.bool,
  upIsGood: PropTypes.bool,
};

NotInFsc.propTypes = {
  range: rangeShape.isRequired,
  firm: PropTypes.string,
  // Counts come from /work/aum/summary's `coverage` block; the component renders
  // nothing at all when households_not_in_fsc is 0 or absent.
  coverage: PropTypes.shape({
    households_not_in_fsc: PropTypes.number,
    net_flow_not_in_fsc: PropTypes.number,
  }),
};

PlatformSplit.propTypes = {
  range: rangeShape.isRequired,
  firm: PropTypes.string,
};

StyleSplit.propTypes = {
  range: rangeShape.isRequired,
  firm: PropTypes.string,
};

Breakdown.propTypes = {
  range: rangeShape.isRequired,
  firm: PropTypes.string,
};

AdvisorRows.propTypes = {
  officeId: PropTypes.string.isRequired,
  range: rangeShape.isRequired,
  // The shared cell style, so advisor rows line up with the firm rows above them.
  numStyle: PropTypes.object,
  hasAum: PropTypes.bool,
  hasCurrentAum: PropTypes.bool,
};

HouseholdRows.propTypes = {
  advisor: PropTypes.string.isRequired,
  firm: PropTypes.string,
  range: rangeShape.isRequired,
  // Column count of the parent table, so the detail row spans it exactly.
  span: PropTypes.number.isRequired,
};

DownloadCsv.propTypes = {
  range: rangeShape.isRequired,
  firm: PropTypes.string,
  advisor: PropTypes.string,
};
